import { useAnimation } from '../common/hooks/useAnimation';
import { useRef } from 'react';
import { useAnimations, useTexture } from '@react-three/drei';
import { useSelector, useDispatch } from 'react-redux';
import * as C from '../common/constants';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';

const Model = (props) => {
  const ref = useRef(null);
  const { bulbRedRef, bulbGreenRef, data } = props;
  const { nodes, scene, materials, animations } = data;

  const paintData = useSelector((state: any) => state.app[C.SET_PAINT]);
  const isSkyVisible = useSelector((state: any) => state.app[C.SET_SKY]);

  const [colorMap, displacementMap, normalMap, roughnessMap, aoMap] =
    useTexture([
      './assets/glossiness.jpeg',
      './assets/glossiness.jpeg',
      './assets/glossiness.jpeg',
      './assets/glossiness.jpeg',
      './assets/occlusion.png',
    ]);

  const { actions, mixer, names } = useAnimations(animations, ref);
  useAnimation(actions, names);

  if (!scene) {
    return null;
  }

  const getBulbs = () => {
    return (
      <>
        <mesh
          name="wing_light_point_left"
          ref={bulbRedRef}
          geometry={nodes.wing_light_point_left.geometry}
          position={[3.276, -0.218, 0.031]}
        >
          <meshStandardMaterial
            color={'red'}
            emissive={'red'}
            emissiveIntensity={200}
            toneMapped={false}
          />
        </mesh>
        <mesh
          name="wing_light_point_right"
          ref={bulbGreenRef}
          geometry={nodes.wing_light_point_right.geometry}
          position={[3.276, -0.218, 0.031]}
        >
          <meshStandardMaterial
            color={'green'}
            emissive={'green'}
            emissiveIntensity={200}
            toneMapped={false}
          />
        </mesh>
      </>
    );
  };

  const getWarehouse = () => {
    if (isSkyVisible) {
      return null;
    }

    return (
      <mesh
        name="Ss"
        castShadow
        receiveShadow
        geometry={nodes.Ss.geometry}
        // material={materials.Material}
        position={[0, -1.4, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        scale={[0.3, 0.3, 0.17]}
      >
        <meshStandardMaterial
          color={COLORS.nBg}
          emissive={COLORS.nBg}
          // emissiveIntensity={1}
          toneMapped={false}
          metalness={0.1}
          roughness={0.2}
          aoMap={aoMap}
          roughnessMap={roughnessMap}
          // normalMap={normalMap}
          // colorMap, displacementMap, normalMap, roughnessMap, aoMap
        />
      </mesh>
    );
  };

  const getPaint = () => {
    switch (paintData?.id) {
      case 1: {
        return (
          <>
            <mesh
              name="1_plane_elevator"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_elevator'].geometry}
              material={materials.sh1_grey}
              position={[0.122, 0.004, -0.711]}
            />
            <mesh
              name="1_plane_elevator_end"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_elevator_end'].geometry}
              material={materials.sh1_grey}
              position={[0.122, 0.004, -0.711]}
            />
            <group name="1_plane_rudder" position={[0, 0.621, -3.727]}>
              <mesh
                name="1_plane_rudder_1"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_rudder_1'].geometry}
                material={materials.sh1_dark}
              />
              <mesh
                name="1_plane_rudder_2"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_rudder_2'].geometry}
                material={materials.sh1_grey}
              />
            </group>
            <group name="1_plane_flaps" position={[0.122, 0.004, -0.711]}>
              <mesh
                name="1_plane_flaps_1"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_flaps_1'].geometry}
                material={materials.sh1_base}
              />
              <mesh
                name="1_plane_flaps_2"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_flaps_2'].geometry}
                material={materials.sh1_red}
              />
              <mesh
                name="1_plane_flaps_3"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_flaps_3'].geometry}
                material={materials.sh1_dark}
              />
            </group>
            <mesh
              name="1_plane_bottom_vent"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_bottom_vent'].geometry}
              material={materials.sh1_base}
              position={[0, -0.004, 2.055]}
            />
            <group
              name="1_plane_aileron"
              position={[0, -0.271, -0.329]}
              rotation={[Math.PI / 2, 0, 0]}
            >
              <mesh
                name="1_plane_aileron_1"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_aileron_1'].geometry}
                material={materials.sh1_base}
              />
              <mesh
                name="1_plane_aileron_2"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_aileron_2'].geometry}
                material={materials.sh1_red}
              />
              <mesh
                name="1_plane_aileron_3"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_aileron_3'].geometry}
                material={materials.sh1_dark}
              />
            </group>
            <mesh
              name="1_plane_wing_right_logo"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_wing_right_logo'].geometry}
              material={materials.sh1_dark}
              position={[0, 0.003, 2.055]}
            />
            <mesh
              name="1_plane_wing_left_logo"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_wing_left_logo'].geometry}
              material={materials.sh1_dark}
              position={[0, 0.001, 2.055]}
            />
            <mesh
              name="1_plane_body_logo_left"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_body_logo_left'].geometry}
              material={materials.sh1_dark}
              position={[0.002, -0.004, 2.055]}
            />
            <mesh
              name="1_plane_tail_logo_left"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_tail_logo_left'].geometry}
              material={materials.sh1_grey}
              position={[0.001, -0.004, 2.055]}
            />
            <mesh
              name="1_plane_tail_logo_right"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_tail_logo_right'].geometry}
              material={materials.sh1_grey}
              position={[-0.001, -0.004, 2.055]}
            />
            <group name="1_plane_body" position={[0, 0.247, -1.039]}>
              <mesh
                name="1_plane_body_1"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_body_1'].geometry}
                material={materials.sh1_base}
              />
              <mesh
                name="1_plane_body_2"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_body_2'].geometry}
                material={materials.sh1_dark}
              />
              <mesh
                name="1_plane_body_3"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_body_3'].geometry}
                material={materials.sh1_grey}
              />
              <mesh
                name="1_plane_body_4"
                castShadow
                receiveShadow
                geometry={nodes['1_plane_body_4'].geometry}
                material={materials.sh1_red}
              />
            </group>
            <mesh
              name="1_plane_body_logo_bottom"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_body_logo_bottom'].geometry}
              material={materials.sh1_red}
              position={[0, 0.245, -1.039]}
            />
            <mesh
              name="1_plane_nose"
              castShadow
              receiveShadow
              geometry={nodes['1_plane_nose'].geometry}
              material={materials.sh1_black}
              position={[0, 0, 2.124]}
            />
          </>
        );
      }
      case 2: {
        return (
          <>
            <mesh
              name="2_plane_nose"
              castShadow
              receiveShadow
              geometry={nodes['2_plane_nose'].geometry}
              material={materials.sh2_base}
              position={[0, 0, 2.124]}
            />
            <mesh
              name="2_plane_flaps"
              castShadow
              receiveShadow
              geometry={nodes['2_plane_flaps'].geometry}
              material={materials.sh2_base}
              position={[0.122, 0.004, -0.711]}
            />
            <mesh
              name="2_plane_elevator_end"
              castShadow
              receiveShadow
              geometry={nodes['2_plane_elevator_end'].geometry}
              material={materials.sh2_base}
              position={[0.122, 0.004, -0.711]}
            />
            <mesh
              name="2_plane_elevator"
              castShadow
              receiveShadow
              geometry={nodes['2_plane_elevator'].geometry}
              material={materials.sh2_base}
              position={[0.122, 0.004, -0.711]}
            />
            <group name="2_plane_body" position={[0, 0.247, -1.039]}>
              <mesh
                name="2_plane_body_1"
                castShadow
                receiveShadow
                geometry={nodes['2_plane_body_1'].geometry}
                material={materials.sh2_base}
              />
              <mesh
                name="2_plane_body_2"
                castShadow
                receiveShadow
                geometry={nodes['2_plane_body_2'].geometry}
                material={materials.sh2_blue}
              />
              <mesh
                name="2_plane_body_3"
                castShadow
                receiveShadow
                geometry={nodes['2_plane_body_3'].geometry}
                material={materials.sh2_grey}
              />
              <mesh
                name="2_plane_body_4"
                castShadow
                receiveShadow
                geometry={nodes['2_plane_body_4'].geometry}
                material={materials.sh2_dark}
              />
            </group>
            <mesh
              name="2_plane_aileron"
              castShadow
              receiveShadow
              geometry={nodes['2_plane_aileron'].geometry}
              material={materials.sh2_base}
              position={[0.122, 0.004, -0.711]}
            />
            <group name="2_plane_rudder" position={[0, 0.621, -3.727]}>
              <mesh
                name="2_plane_rudder_1"
                castShadow
                receiveShadow
                geometry={nodes['2_plane_rudder_1'].geometry}
                material={materials.sh2_base}
              />
              <mesh
                name="2_plane_rudder_2"
                castShadow
                receiveShadow
                geometry={nodes['2_plane_rudder_2'].geometry}
                material={materials.sh2_blue}
              />
              <mesh
                name="2_plane_rudder_3"
                castShadow
                receiveShadow
                geometry={nodes['2_plane_rudder_3'].geometry}
                material={materials.sh2_grey}
              />
            </group>
          </>
        );
      }
      case 3: {
        return (
          <>
            <mesh
              name="3_plane_nose"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_nose'].geometry}
              material={materials.sh3_base}
              position={[0, 0, 2.124]}
            />
            <mesh
              name="3_plane_elevator"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_elevator'].geometry}
              material={materials.sh3_base}
              position={[0.122, 0.004, -0.711]}
            />
            <mesh
              name="3_plane_elevator_end"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_elevator_end'].geometry}
              material={materials.sh3_base}
              position={[0.122, 0.004, -0.711]}
            />
            <group name="3_plane_body" position={[0, 0.247, -1.039]}>
              <mesh
                name="3_plane_body_1"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_body_1'].geometry}
                material={materials.sh3_blue}
              />
              <mesh
                name="3_plane_body_2"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_body_2'].geometry}
                material={materials.sh3_grey}
              />
              <mesh
                name="3_plane_body_3"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_body_3'].geometry}
                material={materials.sh3_base}
              />
            </group>
            <mesh
              name="3_plane_flaps"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_flaps'].geometry}
              material={materials.sh3_base}
              position={[0.122, 0.004, -0.711]}
            />
            <mesh
              name="3_plane_aileron"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_aileron'].geometry}
              material={materials.sh3_base}
              position={[0.122, 0.004, -0.711]}
            />
            <group name="3_plane_rudder" position={[0, 0.621, -3.727]}>
              <mesh
                name="3_plane_rudder_1"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_rudder_1'].geometry}
                material={materials.sh3_base}
              />
              <mesh
                name="3_plane_rudder_2"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_rudder_2'].geometry}
                material={materials.sh3_blue}
              />
              <mesh
                name="3_plane_rudder_3"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_rudder_3'].geometry}
                material={materials.sh3_dark}
              />
            </group>
            <mesh
              name="3_plane_bottom_vent"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_bottom_vent'].geometry}
              material={materials.sh3_base}
              position={[0, 0.247, -1.039]}
            />
            <group name="3_plane_logo_left" position={[0.002, 0.247, -1.039]}>
              <mesh
                name="3_plane_logo_left_1"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_logo_left_1'].geometry}
                material={materials.sh3_blue}
              />
              <mesh
                name="3_plane_logo_left_2"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_logo_left_2'].geometry}
                material={materials.sh3_dark}
              />
            </group>
            <mesh
              name="3_plane_tail_logo_left"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_tail_logo_left'].geometry}
              material={materials.sh3_dark}
              position={[0.002, 0.247, -1.039]}
            />
            <mesh
              name="3_plane_tail_logo_right"
              castShadow
              receiveShadow
              geometry={nodes['3_plane_tail_logo_right'].geometry}
              material={materials.sh3_dark}
              position={[-0.002, 0.247, -1.039]}
            />
            <group name="3_plane_wing_logo_left" position={[0, 0.248, -1.039]}>
              <mesh
                name="3_plane_wing_logo_left_1"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_wing_logo_left_1'].geometry}
                material={materials.sh3_blue}
              />
              <mesh
                name="3_plane_wing_logo_left_2"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_wing_logo_left_2'].geometry}
                material={materials.sh3_dark}
              />
            </group>
            <group name="3_plane_wing_logo_right" position={[0, 0.248, -1.039]}>
              <mesh
                name="3_plane_wing_logo_right_1"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_wing_logo_right_1'].geometry}
                material={materials.sh3_blue}
              />
              <mesh
                name="3_plane_wing_logo_right_2"
                castShadow
                receiveShadow
                geometry={nodes['3_plane_wing_logo_right_2'].geometry}
                material={materials.sh3_dark}
              />
            </group>
          </>
        );
      }
      case 4: {
        return (
          <>
            <mesh
              name="4_chasis_front_gates"
              castShadow
              receiveShadow
              geometry={nodes['4_chasis_front_gates'].geometry}
              material={materials.sh4_red}
              position={[-0.076, -0.465, 1.081]}
              rotation={[-0.084, -0.075, -1.577]}
            />
            <mesh
              name="4_chasis_back_gates"
              castShadow
              receiveShadow
              geometry={nodes['4_chasis_back_gates'].geometry}
              material={materials.sh4_black}
              position={[0.829, -0.794, -0.125]}
            />
            <mesh
              name="4_plane_nose"
              castShadow
              receiveShadow
              geometry={nodes['4_plane_nose'].geometry}
              material={materials.sh4_black}
              position={[0, 0, 2.124]}
            />
            <group name="4_plane_rudder" position={[0, 0.621, -3.727]}>
              <mesh
                name="4_plane_rudder_1"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_rudder_1'].geometry}
                material={materials.sh4_base}
              />
              <mesh
                name="4_plane_rudder_2"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_rudder_2'].geometry}
                material={materials.sh4_black}
              />
            </group>
            <group name="4_plane_flaps" position={[0.122, 0.004, -0.711]}>
              <mesh
                name="4_plane_flaps_1"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_flaps_1'].geometry}
                material={materials.sh4_base}
              />
              <mesh
                name="4_plane_flaps_2"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_flaps_2'].geometry}
                material={materials.sh4_black}
              />
            </group>
            <group
              name="4_plane_elevator_end"
              position={[0.122, 0.004, -0.711]}
            >
              <mesh
                name="4_plane_elevator_end_1"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_elevator_end_1'].geometry}
                material={materials.sh4_base}
              />
              <mesh
                name="4_plane_elevator_end_2"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_elevator_end_2'].geometry}
                material={materials.sh4_red}
              />
              <mesh
                name="4_plane_elevator_end_3"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_elevator_end_3'].geometry}
                material={materials.sh4_black}
              />
            </group>
            <group name="4_plane_elevator" position={[0.122, 0.004, -0.711]}>
              <mesh
                name="4_plane_elevator_1"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_elevator_1'].geometry}
                material={materials.sh4_base}
              />
              <mesh
                name="4_plane_elevator_2"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_elevator_2'].geometry}
                material={materials.sh4_red}
              />
              <mesh
                name="4_plane_elevator_3"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_elevator_3'].geometry}
                material={materials.sh4_black}
              />
            </group>
            <group name="4_plane_body">
              <mesh
                name="4_plane_body_1"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_body_1'].geometry}
                material={materials.sh4_red}
              />
              <mesh
                name="4_plane_body_2"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_body_2'].geometry}
                material={materials.sh4_base}
              />
              <mesh
                name="4_plane_body_3"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_body_3'].geometry}
                material={materials.sh4_black}
              />
            </group>
            <group name="4_plane_aileron" position={[0.122, 0.004, -0.711]}>
              <mesh
                name="4_plane_aileron_1"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_aileron_1'].geometry}
                material={materials.sh4_base}
              />
              <mesh
                name="4_plane_aileron_2"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_aileron_2'].geometry}
                material={materials.sh4_black}
              />
            </group>
            <group name="4_plane_left_logo" position={[0.317, 0.058, 0.887]}>
              <mesh
                name="4_plane_left_logo_1"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_left_logo_1'].geometry}
                material={materials.sh4_base}
              />
              <mesh
                name="4_plane_left_logo_2"
                castShadow
                receiveShadow
                geometry={nodes['4_plane_left_logo_2'].geometry}
                material={materials.sh4_black}
              />
            </group>
            <mesh
              name="4_plane_logo_wing_left"
              castShadow
              receiveShadow
              geometry={nodes['4_plane_logo_wing_left'].geometry}
              material={materials.sh4_black}
              position={[0, 0.248, -1.039]}
            />
            <mesh
              name="4_plane_logo_wing_right"
              castShadow
              receiveShadow
              geometry={nodes['4_plane_logo_wing_right'].geometry}
              material={materials.sh4_black}
              position={[0, 0.248, -1.039]}
            />
            <mesh
              name="4_plane_logo_bottom_body"
              castShadow
              receiveShadow
              geometry={nodes['4_plane_logo_bottom_body'].geometry}
              material={materials.sh4_red}
              position={[0, -0.001, 0]}
            />
            <mesh
              name="4_plane_logo_bottom_aileron"
              castShadow
              receiveShadow
              geometry={nodes['4_plane_logo_bottom_aileron'].geometry}
              material={materials.sh4_red}
              position={[0.122, 0.002, -0.711]}
            />
            <mesh
              name="4_plane_logo_bottom_flaps"
              castShadow
              receiveShadow
              geometry={nodes['4_plane_logo_bottom_flaps'].geometry}
              material={materials.sh4_red}
              position={[0.122, 0.002, -0.711]}
            />
          </>
        );
      }
    }
    return null;
  };

  return (
    <group ref={ref} {...props} dispose={null}>
      {getBulbs()}
      {getPaint()}
      {getWarehouse()}
      <group name="Scene">
        {/* <pointLight
          name="Lamp002"
          intensity={100}
          decay={2}
          position={[0.275, 3.816, 0.282]}
          rotation={[-1.568, -0.001, 1.943]}
          scale={0.66}
        />
        <pointLight
          name="Lamp001"
          intensity={100}
          decay={2}
          position={[-1.067, 6.522, -0.084]}
          rotation={[Math.PI, -0.567, 1.373]}
          scale={0.66}
        />
        <spotLight
          name="Lamp"
          intensity={100}
          angle={Math.PI / 4}
          penumbra={1}
          decay={2}
          position={[-3.164, 6.791, 0.976]}
          rotation={[-1.568, -0.001, 1.943]}
          scale={0.261}
        /> */}
        <mesh
          name="plane_side_net"
          castShadow
          receiveShadow
          geometry={nodes.plane_side_net.geometry}
          material={materials.dark}
          position={[0.268, -0.117, 1.408]}
        />
        <mesh
          name="plane_side_spoilers"
          castShadow
          receiveShadow
          geometry={nodes.plane_side_spoilers.geometry}
          material={materials.dark}
          position={[0.318, -0.068, 1.284]}
        />
        <mesh
          name="plane_glass"
          castShadow
          receiveShadow
          geometry={nodes.plane_glass.geometry}
          material={materials.cockpit_glass}
          position={[0.122, 0.004, -0.711]}
        />
        <mesh
          name="wing_light_glass"
          castShadow
          receiveShadow
          geometry={nodes.wing_light_glass.geometry}
          material={materials.light}
          position={[0.122, 0.004, -0.711]}
        >
          <meshStandardMaterial
            color="#fff"
            transparent
            opacity={0.3}
            roughness={0.3}
            metalness={1.0}
          />
        </mesh>
        <mesh
          name="wing_light_bulb"
          castShadow
          receiveShadow
          geometry={nodes.wing_light_bulb.geometry}
          material={materials.light_black}
          position={[3.279, -0.218, 0.007]}
        />
        {/* <mesh
          name="wing_light_point_left"
          castShadow
          receiveShadow
          geometry={nodes.wing_light_point_left.geometry}
          material={materials.light}
          position={[3.276, -0.218, 0.031]}
        />
        <mesh
          name="wing_light_point_right"
          castShadow
          receiveShadow
          geometry={nodes.wing_light_point_right.geometry}
          material={materials.light}
          position={[3.276, -0.218, 0.031]}
        /> */}
        <mesh
          name="cockpit"
          castShadow
          receiveShadow
          geometry={nodes.cockpit.geometry}
          material={materials.salon}
          position={[0, 0.247, -1.039]}
          rotation={[Math.PI / 2, 0, 0]}
        />
        <mesh
          name="Cube003"
          castShadow
          receiveShadow
          geometry={nodes.Cube003.geometry}
          material={materials.dark}
          position={[2.141, -0.335, -0.399]}
        />
        <mesh
          name="Cube006"
          castShadow
          receiveShadow
          geometry={nodes.Cube006.geometry}
          material={materials.dark}
          position={[1.964, -0.381, -0.128]}
          rotation={[0, 0, -Math.PI / 2]}
        />
        <mesh
          name="Cube007"
          castShadow
          receiveShadow
          geometry={nodes.Cube007.geometry}
          material={materials.dark}
          position={[2.039, -0.331, -0.262]}
        />
        <mesh
          name="Cube015"
          castShadow
          receiveShadow
          geometry={nodes.Cube015.geometry}
          material={materials.dark}
          position={[0.512, -0.449, -0.548]}
          rotation={[2.679, 0.04, 3.115]}
        />
        <mesh
          name="Cube016"
          castShadow
          receiveShadow
          geometry={nodes.Cube016.geometry}
          material={materials.dark}
          position={[0.511, -0.492, -0.428]}
          rotation={[-2.376, -0.011, 3.095]}
          scale={[1, 0.801, 1]}
        />
        <mesh
          name="Cylinder005"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder005.geometry}
          material={materials.base_black}
          position={[0.516, -0.572, -0.516]}
          rotation={[-2.79, 0.008, 1.524]}
        />
        <mesh
          name="plane_blades_3"
          castShadow
          receiveShadow
          geometry={nodes.plane_blades_3.geometry}
          material={materials.blades3}
          position={[0.001, 0.002, 2.127]}
        />

        <mesh
          name="0_chasis_back_gates"
          castShadow
          receiveShadow
          geometry={nodes['0_chasis_back_gates'].geometry}
          material={materials.gates_base}
          position={[0.829, -0.794, -0.125]}
        />
        <mesh
          name="BezierCurve"
          castShadow
          receiveShadow
          geometry={nodes.BezierCurve.geometry}
          material={materials.wire}
          position={[0.753, -1, -0.147]}
        />
        <mesh
          name="chasis_back_screws"
          castShadow
          receiveShadow
          geometry={nodes.chasis_back_screws.geometry}
          material={materials.metal}
          position={[0.743, -0.672, -0.125]}
        />
        <mesh
          name="Cube"
          castShadow
          receiveShadow
          geometry={nodes.Cube.geometry}
          material={materials.metal}
          position={[0.754, -0.963, -0.159]}
        />
        <mesh
          name="Cube009"
          castShadow
          receiveShadow
          geometry={nodes.Cube009.geometry}
          material={materials.metal}
          position={[0.766, -0.548, -0.125]}
        />
        <mesh
          name="Cube010"
          castShadow
          receiveShadow
          geometry={nodes.Cube010.geometry}
          material={materials.metal}
          position={[0.642, -0.421, -0.125]}
        />
        <mesh
          name="Cube021"
          castShadow
          receiveShadow
          geometry={nodes.Cube021.geometry}
          material={materials.metal}
          position={[0.745, -0.736, -0.126]}
        />
        <mesh
          name="Cube022"
          castShadow
          receiveShadow
          geometry={nodes.Cube022.geometry}
          material={materials.metal}
          position={[0.709, -0.657, -0.125]}
        />
        <mesh
          name="Cylinder"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder.geometry}
          material={materials.metal}
          position={[0.795, -0.459, -0.122]}
        />
        <mesh
          name="Cylinder001"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder001.geometry}
          material={materials.metal}
          position={[0.78, -0.965, -0.125]}
        />
        <mesh
          name="Cylinder003"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder003.geometry}
          material={materials.metal}
          position={[0.795, -0.459, -0.122]}
        />
        <mesh
          name="Cylinder009"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder009.geometry}
          material={materials.metal}
          position={[0.697, -0.962, -0.096]}
        />
        <mesh
          name="Cylinder010"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder010.geometry}
          material={materials.metal}
          position={[0.804, -0.563, -0.125]}
        />
        <mesh
          name="Cylinder011"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder011.geometry}
          material={materials.metal}
          position={[0.706, -0.455, -0.125]}
        />
        <mesh
          name="Cylinder019"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder019.geometry}
          material={materials.tires}
          position={[0.782, -0.966, -0.125]}
        />
        <mesh
          name="Cylinder022"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder022.geometry}
          material={materials.red}
          position={[0.763, -0.963, -0.125]}
        />
        <mesh
          name="Cylinder023"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder023.geometry}
          material={materials.wire}
          position={[0.782, -0.965, -0.125]}
        />
        <mesh
          name="Cylinder025"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder025.geometry}
          material={materials.wire}
          position={[0.753, -0.929, -0.146]}
        />
        <mesh
          name="chasis_front_screws"
          castShadow
          receiveShadow
          geometry={nodes.chasis_front_screws.geometry}
          material={materials.metal}
          position={[0, -0.643, 1.491]}
        />
        <mesh
          name="Cube011"
          castShadow
          receiveShadow
          geometry={nodes.Cube011.geometry}
          material={materials.metal}
          position={[0, -0.623, 1.523]}
        />
        <mesh
          name="Cube012"
          castShadow
          receiveShadow
          geometry={nodes.Cube012.geometry}
          material={materials.metal}
          position={[-0.001, -0.707, 1.497]}
        />
        <mesh
          name="Cube013"
          castShadow
          receiveShadow
          geometry={nodes.Cube013.geometry}
          material={materials.metal}
          position={[0, -0.389, 1.374]}
        />
        <mesh
          name="Cylinder002"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder002.geometry}
          material={materials.metal}
          position={[-0.073, -0.939, 1.485]}
        />
        <mesh
          name="Cylinder012"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder012.geometry}
          material={materials.tires}
          position={[0.002, -0.941, 1.485]}
        />
        <mesh
          name="Cylinder013"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder013.geometry}
          material={materials.metal}
          position={[-0.001, -0.94, 1.486]}
        />
        <mesh
          name="Cylinder014"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder014.geometry}
          material={materials.metal}
          position={[0.003, -0.44, 1.408]}
        />
        <mesh
          name="Cylinder015"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder015.geometry}
          material={materials.metal}
          position={[0.002, -0.439, 1.411]}
        />
        <mesh
          name="Cylinder018"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder018.geometry}
          material={materials.metal}
          position={[0, -0.654, 1.533]}
        />
        <mesh
          name="Cylinder020"
          castShadow
          receiveShadow
          geometry={nodes.Cylinder020.geometry}
          material={materials.metal}
          position={[0, -0.405, 1.412]}
        />
        <mesh
          name="0_chasis_front_gates"
          castShadow
          receiveShadow
          geometry={nodes['0_chasis_front_gates'].geometry}
          material={materials.gates_base}
          position={[-0.076, -0.465, 1.081]}
          rotation={[-0.084, -0.075, -1.577]}
        />
      </group>
    </group>
  );
};

export default Model;
